import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, FileText, Eye, Clock, CheckCircle, 
  XCircle, AlertCircle, Edit, Trash2, Send, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  draft: { label: 'Brouillon', icon: Clock, color: 'bg-gray-100 text-gray-700' },
  pending: { label: 'En attente', icon: AlertCircle, color: 'bg-yellow-100 text-yellow-700' },
  published: { label: 'Publié', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Refusé', icon: XCircle, color: 'bg-red-100 text-red-700' },
  revision_needed: { label: 'Révision', icon: AlertCircle, color: 'bg-orange-100 text-orange-700' }
};

export default function DashboardPage() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyArticles();
  }, []);

  const fetchMyArticles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/articles/my`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        setArticles(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch articles:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (articleId) => {
    try {
      const res = await fetch(`${API_URL}/api/articles/${articleId}/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('Article soumis pour évaluation');
        fetchMyArticles();
      }
    } catch (e) {
      toast.error('Erreur lors de la soumission');
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/articles/${articleId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('Article supprimé');
        setArticles(articles.filter(a => a.article_id !== articleId));
      }
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const draftArticles = articles.filter(a => a.status === 'draft' || a.status === 'revision_needed');
  const pendingArticles = articles.filter(a => a.status === 'pending');
  const publishedArticles = articles.filter(a => a.status === 'published');
  const rejectedArticles = articles.filter(a => a.status === 'rejected');

  const ArticleRow = ({ article }) => {
    const config = statusConfig[article.status] || statusConfig.draft;
    const StatusIcon = config.icon;

    return (
      <div 
        className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors"
        data-testid={`article-row-${article.article_id}`}
      >
        <div className="flex-1 min-w-0">
          <h3 className="font-medium truncate">{article.title}</h3>
          <div className="flex items-center gap-3 mt-1">
            <Badge className={`${config.color} border-0 text-xs`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {config.label}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {new Date(article.updated_at).toLocaleDateString('fr-FR')}
            </span>
            {article.views > 0 && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Eye className="w-3 h-3" /> {article.views}
              </span>
            )}
          </div>
          {article.reviewer_comments && (
            <p className="text-xs text-muted-foreground mt-2 p-2 bg-muted rounded">
              Commentaire du reviewer : {article.reviewer_comments}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 ml-4">
          {(article.status === 'draft' || article.status === 'revision_needed') && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/editor/${article.article_id}`)}
                data-testid={`edit-btn-${article.article_id}`}
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSubmit(article.article_id)}
                className="text-primary"
                data-testid={`submit-btn-${article.article_id}`}
              >
                <Send className="w-4 h-4" />
              </Button>
            </>
          )}
          {article.status === 'published' && (
            <Link to={`/articles/${article.article_id}`}>
              <Button variant="ghost" size="sm">
                <Eye className="w-4 h-4" />
              </Button>
            </Link>
          )}
          {article.status !== 'published' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(article.article_id)}
              className="text-destructive"
              data-testid={`delete-btn-${article.article_id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="min-h-screen py-12 px-6 md:px-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold">Tableau de bord</h1>
            <p className="text-muted-foreground mt-1">
              Bienvenue, {user?.name}
            </p>
          </div>
          {(user?.role === 'author' || user?.role === 'admin') && (
            <Link to="/editor/new">
              <Button className="gap-2" data-testid="new-article-btn">
                <Plus className="w-4 h-4" />
                Nouvel article
              </Button>
            </Link>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-primary">{articles.length}</p>
                <p className="text-sm text-muted-foreground">Total</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">{publishedArticles.length}</p>
                <p className="text-sm text-muted-foreground">Publiés</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-yellow-600">{pendingArticles.length}</p>
                <p className="text-sm text-muted-foreground">En attente</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-600">{draftArticles.length}</p>
                <p className="text-sm text-muted-foreground">Brouillons</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Articles Tabs */}
        {user?.role === 'reader' ? (
          <Card>
            <CardHeader>
              <CardTitle className="font-serif">Devenez auteur</CardTitle>
              <CardDescription>
                Pour soumettre des articles, vous devez avoir le rôle d'auteur.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Contactez l'administrateur pour demander le rôle d'auteur et commencer à contribuer au journal.
              </p>
              <Link to="/articles">
                <Button variant="outline" className="gap-2">
                  <FileText className="w-4 h-4" />
                  Parcourir les articles
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="all">
            <TabsList className="mb-6">
              <TabsTrigger value="all">Tous ({articles.length})</TabsTrigger>
              <TabsTrigger value="drafts">Brouillons ({draftArticles.length})</TabsTrigger>
              <TabsTrigger value="pending">En attente ({pendingArticles.length})</TabsTrigger>
              <TabsTrigger value="published">Publiés ({publishedArticles.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="space-y-3">
              {articles.length > 0 ? (
                articles.map(article => <ArticleRow key={article.article_id} article={article} />)
              ) : (
                <EmptyState />
              )}
            </TabsContent>

            <TabsContent value="drafts" className="space-y-3">
              {draftArticles.length > 0 ? (
                draftArticles.map(article => <ArticleRow key={article.article_id} article={article} />)
              ) : (
                <p className="text-center text-muted-foreground py-8">Aucun brouillon</p>
              )}
            </TabsContent>

            <TabsContent value="pending" className="space-y-3">
              {pendingArticles.length > 0 ? (
                pendingArticles.map(article => <ArticleRow key={article.article_id} article={article} />)
              ) : (
                <p className="text-center text-muted-foreground py-8">Aucun article en attente</p>
              )}
            </TabsContent>

            <TabsContent value="published" className="space-y-3">
              {publishedArticles.length > 0 ? (
                publishedArticles.map(article => <ArticleRow key={article.article_id} article={article} />)
              ) : (
                <p className="text-center text-muted-foreground py-8">Aucun article publié</p>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

const EmptyState = () => (
  <Card>
    <CardContent className="py-12">
      <div className="text-center">
        <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="font-serif font-semibold text-lg mb-2">Aucun article</h3>
        <p className="text-muted-foreground mb-4">
          Vous n'avez pas encore créé d'article. Commencez dès maintenant !
        </p>
        <Link to="/editor/new">
          <Button className="gap-2">
            <Plus className="w-4 h-4" />
            Créer mon premier article
          </Button>
        </Link>
      </div>
    </CardContent>
  </Card>
);
